import React, { useState, useEffect } from 'react';
import { RobuxIcon, useAuth } from '../context/AuthContext';
import { ShieldAlert, Plus, Package, Trash2, ArrowRight, Settings } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { DynamicCasePreview, DEFAULT_SHOWCASE } from '../components/CasePreview';
import { ShowcaseEditor } from '../components/ShowcaseEditor';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// ==========================================
// MİKTAR ÇEVİRİCİ (100k, 1m, 1.5b desteği)
// ==========================================
/* ⚠️ BU SAYFA KANONİK KATALOG EDİTÖRÜDÜR.
   Buradaki fiyatlar DAİMA site birimindedir ve BB fiyatları backend'de
   catalog_rate ile ONDAN TÜREİLİR. Aktif cüzdana göre biçimlendirmek
   YANLIŞ olurdu: yönetici katalog fiyatını girer, oyuncunun gördüğü BB
   fiyatı türetilmiş değerdir. Bu yüzden "$" sabit bırakılmıştır. */
const parseAmount = (text) => {
  const match = String(text).match(/[\$]?([\d,.]+)\s*([kmbKMB]?)/);
  if (!match) return 0;
  let value = parseFloat(match[1].replace(/,/g, ''));
  const suffix = match[2].toLowerCase();
  if (suffix === 'k') value *= 1000;
  else if (suffix === 'm') value *= 1000000;
  else if (suffix === 'b') value *= 1000000000;
  return value;
};

// ==========================================
// DYNAMIC CASE PREVIEW COMPONENT
// ==========================================
// 🎨 DynamicCasePreview TEK KAYNAKTAN gelir (components/CasePreview).
// Bu dosyada AYRI bir kopyası vardı; dört dosyada dört kopya
// zamanla ayrışıyor ve her düzeltme dört yere dokunmayı
// gerektiriyordu.

export const AdminCases = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [itemsPool, setItemsPool] = useState([]);
  const [loading, setLoading] = useState(true);

  // Price 'text' tipinde
  const [newItem, setNewItem] = useState({ name: '', image_url: '', price: '', color: '#facc15' });
  const [newCase, setNewCase] = useState({ name: '', image_url: 'color:#3b82f6' }); 
  // 🎨 Vitrin yerleşimi — en nadir 3 eşyanın konum/boyutu.
  // ⚠️ SLOTA bağlı, eşyaya değil: eşya listesi değiştikçe "en nadir 3"
  // değişir; ayarı eşyaya bağlasaydık her düzenlemede yeniden
  // konumlandırmak gerekirdi.
  const [showcase, setShowcase] = useState(DEFAULT_SHOWCASE);
  const [activeSlot, setActiveSlot] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]); 

  useEffect(() => {
    if (user && user.is_admin === false) {
      toast.error("Only administrators can access this page!");
      navigate('/');
      return;
    }
    fetchItemsPool();
  }, [user, navigate]);

  const fetchItemsPool = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/game/cases/admin/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItemsPool(response.data);
    } catch (error) {
      toast.error("Failed to load item pool. Ensure you are an admin.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!newItem.name || !newItem.image_url || !newItem.price) return toast.error("Please fill all fields!");

    const parsedPrice = parseAmount(newItem.price);
    if (parsedPrice <= 0) return toast.error("Invalid price format!");

    try {
      await axios.post(`${BACKEND_URL}/api/game/cases/admin/item`, 
        { ...newItem, price: parsedPrice },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Item added to pool!");
      setNewItem({ name: '', image_url: '', price: '', color: '#facc15' });
      fetchItemsPool();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create item!");
    }
  };

  const toggleItemInCase = (item) => {
    const exists = selectedItems.find(i => i.item_id === item.id);
    if (exists) {
      setSelectedItems(selectedItems.filter(i => i.item_id !== item.id));
    } else {
      setSelectedItems([...selectedItems, { item_id: item.id, name: item.name, price: item.price, color: item.color, image_url: item.image_url, weight: 10 }]);
    }
  };

  const updateItemWeight = (item_id, newWeight) => {
    setSelectedItems(selectedItems.map(i => i.item_id === item_id ? { ...i, weight: parseInt(newWeight) || 0 } : i));
  };

  const calculateExpectedPrice = () => {
    if (selectedItems.length === 0) return 0;
    const totalWeight = selectedItems.reduce((sum, item) => sum + item.weight, 0);
    if (totalWeight === 0) return 0;

    let expectedValue = 0;
    selectedItems.forEach(item => {
      const probability = item.weight / totalWeight;
      expectedValue += (item.price * probability);
    });

    return Math.floor(expectedValue / 0.94);
  };

  const handleCreateCase = async () => {
    if (!newCase.name || !newCase.image_url) return toast.error("Enter case name and select a color!");
    if (selectedItems.length === 0) return toast.error("Add at least 1 item to the case!");

    const payload = {
      name: newCase.name,
      image_url: newCase.image_url,
      items: selectedItems.map(i => ({ item_id: i.item_id, chance_weight: i.weight })),
      // 🎨 Vitrin yerleşimi. Sunucu sınırları ayrıca uyguluyor —
      // istemciden gelen değere güvenilmiyor.
      showcase: showcase
    };

    try {
      await axios.post(`${BACKEND_URL}/api/game/cases/admin/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Case successfully created and published!");
      setNewCase({ name: '', image_url: 'color:#3b82f6' });
      setShowcase(DEFAULT_SHOWCASE);   // yeni kasa için düzeni sıfırla
      setActiveSlot(0);
      setSelectedItems([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create case!");
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0E0F11]" />;

  return (
    <div className="min-h-screen bg-[#0E0F11] pt-24 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        
        <div className="mb-8 flex items-center gap-3">
          <ShieldAlert className="w-8 h-8 text-rose-400" />
          <div>
            <h1 className="text-2xl font-black text-white uppercase tracking-wider">Case Builder</h1>
            <p className="text-zinc-500 text-sm">Administrator Zone</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* LEFT PANEL */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-[#1A1D20] border border-[#2A2D31] rounded-2xl p-6 shadow-xl">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#2ecf6e]" /> Create Item
              </h2>
              <form onSubmit={handleCreateItem} className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-400 font-bold uppercase">Item Name</label>
                  <input type="text" value={newItem.name} onChange={e => setNewItem({...newItem, name: e.target.value})} className="w-full bg-[#15171a] border border-[#2A2D31] rounded-lg px-3 py-2 text-white text-sm mt-1 focus:border-[#B059E8] outline-none" placeholder="e.g. Dragon Lore" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 font-bold uppercase">Image URL (.png)</label>
                  <input type="text" value={newItem.image_url} onChange={e => setNewItem({...newItem, image_url: e.target.value})} className="w-full bg-[#15171a] border border-[#2A2D31] rounded-lg px-3 py-2 text-white text-sm mt-1 focus:border-[#B059E8] outline-none" placeholder="https://..." />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-400 font-bold uppercase">Price ($)</label>
                    <input type="text" value={newItem.price} onChange={e => setNewItem({...newItem, price: e.target.value})} className="w-full bg-[#15171a] border border-[#2A2D31] rounded-lg px-3 py-2 text-white text-sm mt-1 focus:border-[#B059E8] outline-none" placeholder="e.g. 1.5m, 100k" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-400 font-bold uppercase">Rarity Color</label>
                    <select value={newItem.color} onChange={e => setNewItem({...newItem, color: e.target.value})} className="w-full bg-[#15171a] border border-[#2A2D31] rounded-lg px-3 py-2 text-white text-sm mt-1 focus:border-[#B059E8] outline-none">
                      <option value="#facc15">Legendary (Yellow)</option>
                      <option value="#fb7185">Covert (Red)</option>
                      <option value="#c084fc">Rare (Purple)</option>
                      <option value="#60a5fa">Common (Blue)</option>
                      <option value="#4ade80">Basic (Green)</option>
                    </select>
                  </div>
                </div>
                <button type="submit" className="w-full py-2.5 bg-[#16A34A]/10 hover:bg-[#16A34A]/20 text-[#2ecf6e] border border-[#16A34A]/30 rounded-lg font-bold transition-colors">
                  Add to Pool
                </button>
              </form>
            </div>

            <div className="bg-[#1A1D20] border border-[#2A2D31] rounded-2xl p-6 shadow-xl h-[500px] flex flex-col">
              <h2 className="text-lg font-bold text-white mb-4">Current Item Pool</h2>
              <div className="flex-1 overflow-y-auto space-y-2 pr-2 scrollbar-thin">
                {itemsPool.map(item => {
                  const isSelected = selectedItems.find(i => i.item_id === item.id);
                  return (
                    <div 
                      key={item.id} 
                      onClick={() => toggleItemInCase(item)}
                      className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? 'bg-[#B059E8]/10 border-[#B059E8]' : 'bg-[#15171a] border-[#2A2D31] hover:border-[#3a3d43]'}`}
                    >
                      <div className="flex items-center gap-3">
                        <img src={item.image_url} alt={item.name} className="w-10 h-10 object-contain drop-shadow-md" />
                        <div>
                          <p className="text-sm font-bold text-white" style={{ color: item.color }}>{item.name}</p>
                          <p className="text-xs text-[#2ecf6e] mm-num">{item.price.toLocaleString()}<RobuxIcon size={12} className="ml-1" /></p>
                        </div>
                      </div>
                      {isSelected && <div className="w-4 h-4 rounded-full bg-[#B059E8] shadow-[0_0_10px_rgba(176,89,232,0.8)]" />}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT PANEL: CASE DESIGN & PREVIEW */}
          <div className="lg:col-span-2">
            <div className="bg-[#1A1D20] border border-[#2A2D31] rounded-2xl p-8 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                <Settings className="w-64 h-64 animate-spin-slow" />
              </div>

              <h2 className="text-2xl font-black text-white uppercase mb-6 flex items-center gap-3">
                <Package className="w-6 h-6 text-[#B059E8]" />
                Design Case
              </h2>

              <div className="grid grid-cols-2 gap-8 mb-8">
                {/* Inputs */}
                <div className="space-y-6 relative z-10">
                  <div>
                    <label className="text-xs text-zinc-400 font-bold uppercase">Case Name</label>
                    <input type="text" value={newCase.name} onChange={e => setNewCase({...newCase, name: e.target.value})} className="w-full bg-[#15171a] border border-[#2A2D31] rounded-xl px-4 py-3 text-white mt-2 focus:border-[#B059E8] outline-none" placeholder="e.g. Diamond Box" />
                  </div>
                  
                  <div>
                    <label className="text-xs text-zinc-400 font-bold uppercase">Case Color</label>
                    <div className="flex items-center gap-4 mt-2">
                      <input 
                        type="color" 
                        value={newCase.image_url.replace('color:', '')} 
                        onChange={e => setNewCase({...newCase, image_url: `color:${e.target.value}`})}
                        className="w-14 h-14 rounded-lg cursor-pointer bg-[#15171a] border-2 border-[#2A2D31]"
                      />
                      <span className="text-sm mm-num font-bold text-zinc-300 bg-[#15171a] px-3 py-2 rounded-lg border border-[#2A2D31]">
                        {newCase.image_url.replace('color:', '').toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* LIVE PREVIEW + VİTRİN EDİTÖRÜ */}
                <ShowcaseEditor
                  caseColor={newCase.image_url}
                  items={selectedItems}
                  showcase={showcase}
                  setShowcase={setShowcase}
                  activeSlot={activeSlot}
                  setActiveSlot={setActiveSlot}
                />
              </div>

              {/* Selected Items */}
              <div className="bg-[#15171a] border border-[#2A2D31] rounded-xl p-6 mb-8">
                <h3 className="text-sm font-bold text-zinc-300 uppercase mb-4">Selected Items & Odds</h3>
                {selectedItems.length === 0 ? (
                  <p className="text-zinc-600 text-sm text-center py-8">No items in your case yet. Add from the pool.</p>
                ) : (
                  <div className="space-y-3">
                    {selectedItems.map((item, index) => {
                      const totalWeight = selectedItems.reduce((sum, i) => sum + i.weight, 0);
                      const percentage = totalWeight > 0 ? ((item.weight / totalWeight) * 100).toFixed(2) : 0;
                      
                      return (
                        <div key={index} className="flex items-center justify-between bg-[#1A1D20] p-3 rounded-lg border border-white/5">
                          <div className="flex items-center gap-3 w-1/3">
                            <img src={item.image_url} alt={item.name} className="w-8 h-8 object-contain drop-shadow-md" />
                            <span className="text-sm font-bold truncate" style={{ color: item.color }}>{item.name}</span>
                          </div>
                          <div className="w-1/4 text-[#2ecf6e] mm-num text-sm font-bold">{item.price.toLocaleString()}<RobuxIcon size={12} className="ml-1" /></div>
                          <div className="w-1/3 flex items-center gap-3">
                            <span className="text-xs text-zinc-500 font-bold">Weight:</span>
                            <input 
                              type="number" value={item.weight} onChange={(e) => updateItemWeight(item.item_id, e.target.value)}
                              className="w-16 bg-[#15171a] border border-[#2A2D31] rounded px-2 py-1 text-white text-sm outline-none text-center focus:border-[#B059E8]"
                            />
                            <span className="text-xs font-bold text-[#c98bf0] min-w-[40px] text-right">%{percentage}</span>
                          </div>
                          <button onClick={() => toggleItemInCase({id: item.item_id})} className="text-zinc-600 hover:text-rose-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* SAVE & PRICE */}
              <div className="flex items-center justify-between border-t border-[#2A2D31] pt-6">
                <div>
                  <p className="text-xs text-zinc-500 font-bold uppercase mb-1">Calculated Case Price</p>
                  <div className="flex items-end gap-2">
                    <span className="text-4xl font-black text-white">{calculateExpectedPrice().toLocaleString()}</span>
                    <span className="text-xl font-bold text-[#16A34A] mb-1">$</span>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1">* Automatically calculated including 6% House Edge.</p>
                </div>
                <button onClick={handleCreateCase} className="px-8 py-4 text-white font-black uppercase tracking-widest rounded-xl transition-transform hover:scale-105 shadow-[0_0_30px_rgba(176,89,232,0.4)]" style={{ backgroundColor: '#B059E8', backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0px, rgba(0,0,0,0.18) 9px, transparent 9px, transparent 20px)' }}>
                  Publish Case <ArrowRight className="w-5 h-5 ml-2 inline-block" />
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};